import { CommerceAPI } from "commerce-api"
import { StoreModel } from "./utils"
import { Status, StatusMap } from "../types/commerce"
import { RootStore } from "./RootStore"
import { makeObservable, observable, action, flow, reaction } from "mobx"
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { BookRapidDeliveryResponse, RapidDeliveryStoreInitialState, RapidDeliveryReservation } from '../types/store/rapidDelivery'

export class RapidDeliveryStore implements StoreModel {
  api: CommerceAPI
  status: Status
  rootStore: RootStore
  isRapidDeliveryAvailableForPostcode: boolean
  rapidDeliveryShippingCost: number | null
  constructor(rootStore: RootStore, initialState?: RapidDeliveryStoreInitialState) {
    this.api = rootStore.api
    this.status = initialState?.status ?? StatusMap.IDLE
    this.isRapidDeliveryAvailableForPostcode = initialState?.isRapidDeliveryAvailableForPostcode || false
    this.rapidDeliveryShippingCost = initialState?.rapidDeliveryShippingCost || null
    this.rootStore = rootStore

    makeObservable(this, {
      status: observable,
      isRapidDeliveryAvailableForPostcode: observable,
      setRapidDeliveryAvailability: action.bound,
      bookRapidDelivery: flow.bound,
      rapidDeliveryShippingCost: observable,
    })
  }

  setRapidDeliveryAvailability(value: boolean) {
    this.isRapidDeliveryAvailableForPostcode = value
  }

  setRapidDeliveryShippingCost(value: number | null) {
    this.rapidDeliveryShippingCost = value
  }

  *bookRapidDelivery() {
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Rapid-BookDelivery`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )
    const result: BookRapidDeliveryResponse = yield response.json()
    if (result.code !== 'success') {
      throw new Error('Failed to book Rapid Delivery')
    }

    if (result.basket) {
      this.rootStore.basketStore.setBasket(result.basket)
      this.rootStore.globalStore.setSelectedRegion()
    }
  }

  get isRapidDeliveryAvailable() {
    return this.rootStore.globalStore.customSitePreferences?.enableRapidDelivery as boolean
  }

  get rapidDeliveryMaxItems() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryMaxItems as number
  }

  get rapidDeliverySoftReserveTimeLimit() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliverySoftReserveTime as number
  }

  get rapidDeliveryFallback() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryFallback as string
  }

  get rapidDeliveryServiceUnavailableMessage() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryServiceUnavailable as boolean
  }

  get rapidDeliveryPricebookEnabled() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryPricebookEnabled as boolean
  }

  get isRapidDeliveryBooked() {
    return this.rootStore.basketStore.basket?.c_isRapidDeliverySelected ?? false
  }

  get rapidDeliveryDeliveryEstimate() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryDeliveryEstimate as number ?? 60
  }

  get rapidDeliveryReservation(): RapidDeliveryReservation | null {
    const basket = this.rootStore.basketStore.basket
    if (!basket?.c_slotExpiryTime) {
      return null
    }
    return {
      expiryTime: basket.c_slotExpiryTime,
      windowStartTime: basket.c_windowStartTime || null,
      windowEndTime: basket.c_windowEndTime || null,
      shippingCost: basket.shippingItems?.[0].c_deliveryCharge ?? null
    }
  }

  get asJson() {
    return {
      status: this.status,
      isRapidDeliveryBooked: this.isRapidDeliveryBooked,
      isRapidDeliveryAvailableForPostcode: this.isRapidDeliveryAvailableForPostcode,
      rapidDeliveryReservation: this.rapidDeliveryReservation
    }
  }
}